/**
 * Featured collections markup builder — the storefront's copy of the designer's
 * `collectionsMarkup.js`, so the published rail and the canvas preview cannot
 * drift.
 *
 * A rail of collection cover cards, matching SP-main's `collectionCard()` in its
 * `storefront: true` form and the `.ccard` rules. The rail shell is the one
 * Featured brands and products already use, so its CSS is shared.
 *
 * The storefront variant carries no Share button and no product count — sharing
 * is the partner's action, not the shopper's — and "Shop collection →" takes
 * their place.
 */

import { escapeHtml } from '../../utilities/helper';
import { copyOf, accentTextOf } from './bannerMarkup';

/** Seeds from SP-main's `storefrontSeedText('featured-collections')`, so a
 *  cleared field still reads as a real section. */
export const COLLECTIONS_SEED = {
  title: 'Featured collections',
  subtitle: 'A few of our favorite picks, grouped together.',
};

const ARROW_PREV = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>`;
const ARROW_NEXT = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>`;

/* A folder glyph on the card's own gradient, never a broken <img> and never a
   stock photo standing in for a cover the partner never chose (collections carry
   no cover image yet — see `normalizeCollection`). The gradient is a fixed slate,
   not the store's colour: six identical theme-coloured covers read as a colour
   block rather than six collections. */
const COVER_GLYPH = `<span class="glyph"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 7h6l2 2h10v10H3z"/></svg></span>`;

/**
 * The section stored a single `collectionId` before it featured several; the
 * panel writes `collectionIds` now. Both shapes are read here, in one place.
 * Blank strings are dropped as well as nulls — a cleared <select> stores "".
 */
export const normalizeCollectionIds = (block = {}) => {
  const raw = Array.isArray(block?.collectionIds)
    ? block.collectionIds
    : (block?.collectionId != null && block.collectionId !== 0 ? [block.collectionId] : []);

  return raw
    .map((entry) => (entry && typeof entry === 'object' ? entry.id : entry))
    .filter((id) => id != null && String(id).trim() !== '');
};

/**
 * Normalise one API collection record to what a card needs.
 *
 * `logo` is the cover image column on store_collections and is currently left
 * out of the storefront list response (commented out in
 * store-collection.service.js), so every card draws the placeholder. Reading the
 * field anyway means covers appear the day the API returns it. */
export const normalizeCollection = (collection = {}) => ({
  id: collection?.id,
  name: String(collection?.display_name || collection?.name || '').trim(),
  description: String(collection?.description || '').trim(),
  image: String(collection?.logo || collection?.image || '').trim(),
  productCount: collection?.product_count,
});

const hrefOf = (collection) =>
  collection?.id ? `/collections/${encodeURIComponent(collection.id)}` : '#';

const cardHtml = (collection, themeColor) => {
  const name = String(collection?.name || '').trim();
  const description = String(collection?.description || '').trim();
  const image = String(collection?.image || collection?.cover || '').trim();

  const media = image
    ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">`
    : COVER_GLYPH;

  return (
    `<a class="ccard" href="${escapeHtml(hrefOf(collection))}" aria-label="${escapeHtml(name)}">` +
    `<div class="cov">${media}<span class="scrim"></span><span class="cn">${escapeHtml(name)}</span></div>` +
    `<div class="cbody">` +
    // Empty rather than absent when there is no description: `.cd` is the
    // flexible row in the card body, and dropping it moves "Shop collection" up
    // against the cover on that one card.
    (description ? `<p class="cd">${escapeHtml(description)}</p>` : `<p class="cd"></p>`) +
    `<div class="cmeta"><span class="cshop" style="color:#BB341A">Shop collection &rarr;</span></div>` +
    `</div></a>`
  );
};

/**
 * @param {{ title?: string, subtitle?: string, visible?: boolean }} block
 * @param {Array} collections  records already through `normalizeCollection`
 * @param {{ headingFont?: string, themeColor?: string }} options
 * @returns {string} markup, or '' when there is nothing to show
 */
export const buildCollectionsHtml = (block = {}, collections = [], options = {}) => {
  const { headingFont, themeColor } = options;

  // A card with no name is not a collection anyone can read, whatever else the
  // record carries.
  const list = (Array.isArray(collections) ? collections : [])
    .filter((collection) => collection && String(collection.name || '').trim());

  // Nothing picked, or every pick since deleted or unpublished: no section — not
  // an empty frame, and not the canvas's "Select a collection" prompt.
  if (!list.length || block.visible === false) return '';

  const heading = copyOf(block.title) || COLLECTIONS_SEED.title;
  const sub = copyOf(block.subtitle) || COLLECTIONS_SEED.subtitle;
  const headFont = headingFont ? `font-family:${headingFont};` : '';

  return `<section class="sf-sec sf-collections-sec">
  <div class="sf-in">
    <div class="sf-sh">
      <div>
        <h2 style="${headFont}">${escapeHtml(heading)}</h2>
        <div class="sub">${escapeHtml(sub)}</div>
      </div>
      <a class="sf-seeall" href="/all-collections" style="color:${escapeHtml(themeColor ? accentTextOf(themeColor) : '#2C2F32')}">View all collections &rarr;</a>
    </div>
    <div class="sf-railwrap">
      <div class="sf-arrows">
        <button class="sf-arr prev scroll-left" type="button" aria-label="Scroll back">${ARROW_PREV}</button>
        <button class="sf-arr next scroll-right" type="button" aria-label="Scroll forward">${ARROW_NEXT}</button>
      </div>
      <div class="sf-rail cards-scroller">${list.map((collection) => cardHtml(collection, themeColor)).join('')}</div>
    </div>
  </div>
</section>`;
};

export default buildCollectionsHtml;
